import { FieldArray, Formik, useFormikContext } from "formik";
import { useEffect } from "react";
import apiRequest from "../../../utils/admin/apiRequest";
import InputField from "../../ui/form/InputField";
import Button from "../../ui/utils/Button";
import SelectField from "../../ui/form/SelectField";
import usePost from "../../../hooks/api/usePost";
import Alert from "../../ui/utils/Alert";
import useFetch from "../../../hooks/api/useFetch";
import { createVendorStaffSchema } from "../../../validation/admin/vendoValidation";
import { CgAdd } from "react-icons/cg";
import { Trash } from "lucide-react";


// -------------------------------------------------------------------
// LOCATION WATCHER
// -------------------------------------------------------------------
const LocationWatcher = ({
  fetchStates,
  fetchCities,
  setBlankResponse,
  setCityBlankResponse,
}) => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    if (values.countryId) {
      fetchStates({ countryId: values.countryId });
      setFieldValue("stateId", "");
    } else setBlankResponse();
  }, [values.countryId]);

  useEffect(() => {
    if (values.stateId) {
      fetchCities({ stateId: values.stateId });
      setFieldValue("cityId", "");
    } else setCityBlankResponse();
  }, [values.stateId]);

  return null;
};


// -------------------------------------------------------------------
// CORPORATE WATCHER — 3 LEVEL CHAIN (Admin → Corporate Admin → SuperVendor)
// -------------------------------------------------------------------
const CorporateWatcher = ({
  values,
  fetchTowerAdmins,
  clearTowerAdmins,
  fetchSuperVendors,
  clearSuperVendors,
}) => {

  // 1) Head Admin → Corporate Admins
  useEffect(() => {
    values.corporateCodes.forEach((row) => {
      row.adminId
        ? fetchTowerAdmins({ adminId: row.adminId })
        : clearTowerAdmins();
    });
  }, [values.corporateCodes.map((r) => r.adminId).join(",")]);


  // 2) Corporate Admin → Super Vendors
  useEffect(() => {
    values.corporateCodes.forEach((row) => {
      row.corporateId
        ? fetchSuperVendors({ corporateAdminId: row.corporateId })
        : clearSuperVendors();
    });
  }, [values.corporateCodes.map((r) => r.corporateId).join(",")]);

  return null;
};



// ===================================================================
// MAIN COMPONENT START
// ===================================================================
const CreateVendorStaff = ({ closeModal, callback }) => {
  const { error, isLoading, post, serverErrors } = usePost(
    apiRequest.createVendor
  );


  // HEAD ADMIN LIST
  const { data: adminList } = useFetch({
    apiMethod: apiRequest.getHeadAdmin,
    body: { paginate: false },
    autoFetch: true,
  });

  // CORPORATE ADMINS
  const {
    data: towerAdmins,
    refetch: fetchTowerAdmins,
    setBlankResponse: clearTowerAdmins,
  } = useFetch({
    apiMethod: apiRequest.getCorporateAdminByHeadAdmin,
    autoFetch: false,
  });

  // SUPER VENDORS
  const {
    data: superVendors,
    refetch: fetchSuperVendors,
    setBlankResponse: clearSuperVendors,
  } = useFetch({
    apiMethod: apiRequest.getSuperVendorsByCorporateAdmin,
    autoFetch: false,
  });


  // COUNTRY / STATE / CITY
  const { data: countryData } = useFetch({
    apiMethod: apiRequest.getCountry,
    body: { paginate: false },
  });

  const { data: stateData, refetch: fetchStates, setBlankResponse } = useFetch({
    apiMethod: apiRequest.getStateByCountryId,
    autoFetch: false,
  });

  const {
    data: cityData,
    refetch: fetchCities,
    setBlankResponse: setCityBlankResponse,
  } = useFetch({
    apiMethod: apiRequest.getCityByState,
    autoFetch: false,
  });



  // ===============================================================
  // INITIAL FORM VALUES
  // ===============================================================
  const initialValues = {
    name: "",
    countryId: "",
    stateId: "",
    cityId: "",
    mobile: "",
    email: "",
    contactPerson: "",
    userId: "",
    password: "",
    vendorType: "vendor",
    isSuperVendor: false,
    userDetails: [{ name: "", mobile: "", email: "" }],
    corporateCodes: [
      {
        adminId: "",
        corporateId: "",
        superVendorId: "",
        code: "",
        _codes: [], // dynamic code list
      },
    ],
  };


  // ===============================================================
  // UI
  // ===============================================================
  return (
    <div>
      <h2 className="p-4 text-lg font-semibold">Create Vendor Manager Profile</h2>
      <div className="border-b" />

      <Formik
        initialValues={initialValues}
        validationSchema={createVendorStaffSchema}
        validateOnBlur
        validateOnChange
        onSubmit={async (values) => {
          const res = await post({ data: { body: values } });
          if (res.response) callback?.();
        }}
      >
        {({ values, handleChange, handleBlur, handleSubmit, touched, errors }) => (
          <>
            {/* AUTO WATCHERS */}
            <LocationWatcher
              fetchStates={fetchStates}
              fetchCities={fetchCities}
              setBlankResponse={setBlankResponse}
              setCityBlankResponse={setCityBlankResponse}
            />

            <CorporateWatcher
              values={values}
              fetchTowerAdmins={fetchTowerAdmins}
              clearTowerAdmins={clearTowerAdmins}
              fetchSuperVendors={fetchSuperVendors}
              clearSuperVendors={clearSuperVendors}
            />

            <form
              onSubmit={handleSubmit}
              className="text-sm max-h-[80vh] overflow-auto"
            >
              {error && <Alert message={error} type="error" />}


              {/* =========================================================== */}
              {/* BASIC INFO */}
              {/* =========================================================== */}
              <div className="grid grid-cols-1 p-4 gap-2 sm:grid-cols-3">
                <InputField
                  name="name"
                  label="Vendor Manager Name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && errors.name}
                />

                <InputField
                  name="mobile"
                  label="Mobile"
                  value={values.mobile}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.mobile && errors.mobile}
                />

                <InputField
                  name="email"
                  label="Email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && errors.email}
                />

                {/* Country */}
                <SelectField
                  name="countryId"
                  label="Country"
                  value={values.countryId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  options={countryData?.countries?.map((c) => ({
                    value: c._id,
                    label: c.name,
                  }))}
                  error={touched.countryId && errors.countryId}
                />

                {/* State */}
                <SelectField
                  name="stateId"
                  label="State"
                  value={values.stateId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  options={stateData?.states?.map((s) => ({
                    value: s._id,
                    label: s.name,
                  }))}
                  error={touched.stateId && errors.stateId}
                />

                {/* City */}
                <SelectField
                  name="cityId"
                  label="City"
                  value={values.cityId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  options={cityData?.cities?.map((c) => ({
                    value: c._id,
                    label: c.name,
                  }))}
                  error={touched.cityId && errors.cityId}
                />

                <InputField
                  name="contactPerson"
                  label="Contact Person"
                  value={values.contactPerson}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.contactPerson && errors.contactPerson}
                />

                <InputField
                  name="userId"
                  label="User ID"
                  value={values.userId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.userId && errors.userId}
                />

                <InputField
                  type="text"

                  name="password"
                  label="Password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.password && errors.password}
                />


                {/* =========================================================== */}
                {/* USER DETAILS ARRAY */}
                {/* =========================================================== */}
                <div className="col-span-full mt-3">
                  <h3 className="font-semibold mb-2">Partner Details</h3>

                  <FieldArray name="userDetails">
                    {({ push, remove }) => (
                      <>
                        <table className="w-full border">
                          <thead>
                            <tr>
                              <th className="p-2 border">Name</th>
                              <th className="p-2 border">Mobile</th>
                              <th className="p-2 border">Email</th>
                              <th className="p-2 border text-center">Action</th>
                            </tr>
                          </thead>

                          <tbody>
                            {values.userDetails.map((row, index) => (
                              <tr key={index}>
                                <td className="border p-2">
                                  <InputField
                                    name={`userDetails.${index}.name`}
                                    placeholder="Enter name"
                                    value={row.name}
                                    onChange={handleChange}
                                  />
                                </td>

                                <td className="border p-2">
                                  <InputField
                                    name={`userDetails.${index}.mobile`}
                                      placeholder="Enter mobile"
                                    value={row.mobile}
                                    onChange={handleChange}
                                  />
                                </td>

                                <td className="border p-2">
                                  <InputField
                                    name={`userDetails.${index}.email`}
                                      placeholder="Enter email"

                                    value={row.email}
                                    onChange={handleChange}
                                  />
                                </td>

                                <td className="border p-2 text-center">
                                  <button
                                    type="button"
                                    onClick={() => remove(index)}
                                    className="text-red-500"
                                  >
                                    <Trash size={16} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() =>
                              push({ name: "", mobile: "", email: "" })
                            }
                            className="mt-2 px-3 py-1 text-white bg-blue-600 rounded flex items-center gap-2"
                          >
                            <CgAdd /> Add User
                          </button>
                        </div>
                      </>
                    )}
                  </FieldArray>
                </div>


                {/* =========================================================== */}
                {/* CORPORATE CHAIN: Admin → Corporate Admin → Super Vendor → Codes */}
                {/* =========================================================== */}
                <div className="col-span-full mt-3">
                  <h3 className="font-semibold mb-2">Corporate Mapping</h3>

                  <FieldArray name="corporateCodes">
                    {({ push, remove }) => (
                      <>
                        <table className="w-full border">
                          <thead>
                            <tr>
                              <th className="border p-2">Corporate Admin Head</th>
                              <th className="border p-2">Tower Admin</th>
                              <th className="border p-2">Vendor</th>
                              <th className="border p-2">Code</th>
                              {/* <th className="border p-2">Action</th> */}
                            </tr>
                          </thead>

                          <tbody>
                            {values.corporateCodes.map((row, index) => {
                              const adminOptions =
                                adminList?.admins?.map((a) => ({
                                  value: a._id,
                                  label: a.name,
                                })) || [];

                              const corporateAdminOptions =
                                towerAdmins?.admins?.map((a) => ({
                                  value: a._id,
                                  label: a.name,
                                })) || [];

                              const superVendorOptions =
                                superVendors?.vendors?.map((v) => ({
                                  value: v._id,
                                  label: v.name,
                                })) || [];


                              // FILTER CODES from selected superVendor
                              const vendor = superVendors?.vendors?.find(
                                (v) => v._id === row.superVendorId
                              );

                              const filteredCodes =
                                vendor?.corporateCodes?.filter(
                                  (c) => c.corporateId === row.corporateId
                                ) || [];


                              return (
                                <tr key={index}>
                                  {/* ADMIN */}
                                  <td className="border p-2">
                                    <SelectField
                                      name={`corporateCodes.${index}.adminId`}
                                      value={row.adminId}
                                      onChange={(e) => {
                                        handleChange(e);

                                        values.corporateCodes[index].corporateId = "";
                                        values.corporateCodes[index].superVendorId = "";
                                        values.corporateCodes[index].code = "";
                                      }}
                                      options={adminOptions}
                                    />
                                  </td>

                                  {/* CORPORATE ADMIN */}
                                  <td className="border p-2">
                                    <SelectField
                                      name={`corporateCodes.${index}.corporateId`}
                                      value={row.corporateId}
                                      onChange={(e) => {
                                        handleChange(e);

                                        values.corporateCodes[index].superVendorId = "";
                                        values.corporateCodes[index].code = "";

                                        fetchSuperVendors({
                                          corporateAdminId: e.target.value,
                                        });
                                      }}
                                      options={corporateAdminOptions}
                                    />
                                  </td>

                                  {/* SUPER VENDOR */}
                                  <td className="border p-2">
                                    <SelectField
                                      name={`corporateCodes.${index}.superVendorId`}
                                      value={row.superVendorId}
                                      onChange={(e) => {
                                        handleChange(e);

                                        values.corporateCodes[index].code = "";
                                      }}
                                      options={superVendorOptions}
                                    />
                                  </td>

                                  {/* CODE */}
                                  <td className="border p-2">
                                    <SelectField
                                      name={`corporateCodes.${index}.code`}
                                      value={row.code}
                                      onChange={handleChange}
                                      options={filteredCodes.map((c) => ({
                                        value: c.code,
                                        label: c.code,
                                      }))}
                                    />
                                  </td>

                                  {/* REMOVE */}
                                  {/* <td className="border p-2 text-center">
                                    <button
                                      type="button"
                                      onClick={() => remove(index)}
                                      className="text-red-500"
                                    >
                                      <Trash size={17} />
                                    </button>
                                  </td> */}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>

                        {/* <div className="flex justify-end mt-2">
                          <button
                            type="button"
                            onClick={() =>
                              push({
                                adminId: "",
                                corporateId: "",
                                superVendorId: "",
                                code: "",
                              })
                            }
                            className="px-3 py-1 text-white bg-blue-600 rounded flex items-center gap-2"
                          >
                            <CgAdd /> Add Mapping
                          </button>
                        </div> */}
                      </>
                    )}
                  </FieldArray>
                </div>
              </div>


              {/* =========================================================== */}
              {/* SUBMIT BUTTON */}
              {/* =========================================================== */}
              <div className="flex justify-end gap-2 border-t p-4">
                <Button variant="secondary" onClick={closeModal}>
                  Cancel
                </Button>

                <Button type="submit" variant="primary" isLoading={isLoading}>
                  Create Vendor Staff
                </Button>
              </div>

            </form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default CreateVendorStaff;
